import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useBasket } from "@/context/BasketContext";

const NAV_LINKS = [
    { to: "/shop", label: "Shop", testid: "nav-shop-link" },
    { to: "/farms", label: "Our Farms", testid: "nav-farms-link" },
    { to: "/club", label: "The Club", testid: "nav-club-link" },
    { to: "/our-story", label: "Our Story", testid: "nav-our-story-link" },
    { to: "/journal", label: "Journal", testid: "nav-journal-link" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { count, setIsOpen } = useBasket();
    const location = useLocation();
    const isHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const solid = scrolled || !isHome || mobileOpen;

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                solid
                    ? "bg-[#0B0B0C]/95 backdrop-blur-md border-b border-white/[0.08]"
                    : "bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                <div className="flex items-center justify-between h-16 sm:h-20">
                    {/* Left links — desktop */}
                    <nav className="hidden lg:flex items-center gap-8 flex-1">
                        {NAV_LINKS.slice(0, 2).map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className="eyebrow text-[#F6F1E7]/70 hover:text-[#C9B37A] transition-colors duration-200"
                                data-testid={l.testid}
                            >
                                {l.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Mobile menu button */}
                    <button
                        className="lg:hidden text-[#F6F1E7] p-2 -ml-2"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        aria-label="Toggle menu"
                        data-testid="nav-mobile-menu-button"
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>

                    {/* Brand */}
                    <Link to="/" className="text-center group" data-testid="nav-brand-link">
                        <div className="font-serif-display text-lg sm:text-xl font-semibold tracking-[0.28em] text-[#F6F1E7] leading-none">
                            BRITISH
                        </div>
                        <div className="font-serif-display text-lg sm:text-xl font-semibold tracking-[0.28em] text-[#F6F1E7] leading-none mt-0.5">
                            STEAK CO.
                        </div>
                        <div className="font-mono-meta text-[7px] tracking-[0.4em] text-[#C9B37A] mt-1">
                            DRY AGED BEEF
                        </div>
                    </Link>

                    {/* Right links + basket */}
                    <div className="flex items-center gap-8 flex-1 justify-end">
                        <nav className="hidden lg:flex items-center gap-8">
                            {NAV_LINKS.slice(2).map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    className="eyebrow text-[#F6F1E7]/70 hover:text-[#C9B37A] transition-colors duration-200"
                                    data-testid={l.testid}
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </nav>
                        <button
                            onClick={() => setIsOpen(true)}
                            className="relative flex items-center gap-2 text-[#F6F1E7]/80 hover:text-[#C9B37A] transition-colors duration-200 p-2 -mr-2"
                            aria-label={`Open basket, ${count} items`}
                            data-testid="nav-basket-button"
                        >
                            <ShoppingBag size={19} strokeWidth={1.5} />
                            <span className="hidden sm:inline eyebrow">Basket ({count})</span>
                            {count > 0 && (
                                <span className="sm:hidden absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#C9B37A] text-[#0B0B0C] text-[9px] font-bold flex items-center justify-center">
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="lg:hidden bg-[#0B0B0C] border-t border-white/[0.08] px-6 py-6 flex flex-col gap-5">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            className="eyebrow text-[#F6F1E7]/80 hover:text-[#C9B37A] transition-colors"
                            data-testid={`mobile-${l.testid}`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>
            )}
        </header>
    );
};
